import { authHeaders, ApiError } from '@/services/authApi';
import type { DetailLead } from '@/types/detailLead';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export interface PoolLeadSummary {
  id: string;
  title: string;
  name: string;
  companyId: string;
  companyName: string;
  creditCode?: string;
  industry: string;
  region: string;
  leadType: string;
  verifyStatus: string;
  evidenceLevel: string;
  priority: string;
  triggerEvent: string;
  eventDate: string;
  publishDate: string;
  informationSource: string;
}

export interface PoolLeadFacets {
  regions: string[];
  industries: string[];
  leadTypes: string[];
  verifyStatuses: string[];
}

export interface PoolLeadListResult {
  items: PoolLeadSummary[];
  page: number;
  pageSize: number;
  total: number;
}

export interface PoolLeadListQuery {
  keyword?: string;
  region?: string;
  industry?: string;
  leadType?: string;
  verifyStatus?: string;
  companyId?: string;
  companyName?: string;
  page?: number;
  pageSize?: number;
}

async function readApiError(res: Response, fallback: string): Promise<ApiError> {
  const body = await res.json().catch(() => ({}));
  const message = body?.error?.message || fallback;
  const code = body?.error?.code || 'UNKNOWN';
  return new ApiError(message, code, res.status);
}

function toQuery(params: PoolLeadListQuery): string {
  const q = new URLSearchParams();
  if (params.keyword) q.set('keyword', params.keyword);
  if (params.region && params.region !== '全部') q.set('region', params.region);
  if (params.industry && params.industry !== '全部') q.set('industry', params.industry);
  if (params.leadType && params.leadType !== '全部') q.set('lead_type', params.leadType);
  if (params.verifyStatus && params.verifyStatus !== '全部') {
    q.set('verify_status', params.verifyStatus);
  }
  if (params.companyId) q.set('company_id', params.companyId);
  if (params.companyName) q.set('company_name', params.companyName);
  if (params.page) q.set('page', String(params.page));
  if (params.pageSize) q.set('page_size', String(params.pageSize));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export async function listPoolLeads(
  params: PoolLeadListQuery = {},
  signal?: AbortSignal,
): Promise<PoolLeadListResult> {
  const res = await fetch(`${API_BASE_URL}/api/v1/leads${toQuery(params)}`, { signal });
  if (!res.ok) throw await readApiError(res, '加载线索池失败');
  const json = await res.json();
  return json.data as PoolLeadListResult;
}

export async function getPoolLeadFacets(): Promise<PoolLeadFacets> {
  const res = await fetch(`${API_BASE_URL}/api/v1/leads/facets`);
  if (!res.ok) throw await readApiError(res, '加载筛选项失败');
  const json = await res.json();
  return json.data as PoolLeadFacets;
}

export async function getPoolLead(leadId: string): Promise<DetailLead> {
  const res = await fetch(`${API_BASE_URL}/api/v1/leads/${encodeURIComponent(leadId)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await readApiError(res, '加载线索详情失败');
  const json = await res.json();
  const raw = json.data as DetailLead;
  return {
    ...raw,
    verifyStatus: raw.verifyStatus as DetailLead['verifyStatus'],
    evidenceLevel: raw.evidenceLevel as DetailLead['evidenceLevel'],
    priority: raw.priority as DetailLead['priority'],
  };
}
